/**
 * `ModulesPermissionsArray` — SwaggerHub `Last-Modified` 02-10-2026 17:34 UTC.
 *
 * One band is `{ module, permissions }`. `module` names a top menu tab
 * (Rachel, 03-10-2026); uppercase keys come from the 03-10-2026 enum agreement (Settings GAPS B5).
 * Neither member is required. An
 * omitted array is not an empty one. The catalogue read requires the array
 * itself (`GET /v1/system/permissions` payload). `GrantedRolesObj.modules`
 * does not.
 *
 * Roster and session module names are not drawn (the permission catalogue
 * draws its band name), and none is copied onto
 * `session.modules` (that list is the edition). The session gate still
 * matches a flat list of permission strings: `grantsFromModules` flattens
 * the bands when the key is present.
 */
import { z } from "zod/mini";

export const modulePermissionsSchema = z.object({
  module: z.optional(z.string()),
  permissions: z.optional(z.array(z.string())),
});

/** The array. Callers wrap it in `z.optional` where the document leaves the key off. */
export const modulesPermissionsSchema = z.array(modulePermissionsSchema);

export type ModulePermissions = z.infer<typeof modulePermissionsSchema>;

/**
 * The strings the gate matches. An omitted `modules` was not supplied.
 * A present array, including `[]`, was. Order is the document's order,
 * band by band. Codes are not deduped.
 */
export function grantsFromModules(modules: readonly { permissions?: readonly string[] }[] | undefined): {
  permissions: string[];
  permissionsSupplied: boolean;
} {
  if (modules === undefined) return { permissions: [], permissionsSupplied: false };
  return {
    permissions: modules.flatMap((band) => [...(band.permissions ?? [])]),
    permissionsSupplied: true,
  };
}
